import React, { useEffect, useState } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { Calendar, Users, Trophy, Target } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import TeamRegistrationForm, { TeamRegistrationData } from './TeamRegistrationForm';
import CustomLoader from './CustomLoader';
import { useApiClient } from '../services/api';
import { useAppContext } from '../context/AppContext';
import { setPendingRegistration } from '../utils/pendingRegistration';
import MarkdownRenderer from './MarkdownRenderer';

interface Prize {
  amount: number;
  currency: string;
}

interface Bounty {
  id: string;
  title: string;
  description: string;
  prizes: Prize[];
  status: string;
  difficulty: string;
  categories: string[];
  maxWinners: number;
  currentWinners: number;
  startDate: Date | string;
  endDate: Date | string;
  participants: number;
}

interface Event {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  startDate: Date | string;
  endDate: Date | string;
  registrationDeadline: Date | string;
  maxParticipants: number;
  currentParticipants: number;
  type: string;
  bounties: Bounty[];
}

interface EventsBountiesData {
  events: Event[];
  totalEvents: number;
  totalBounties: number;
}

export const PublicEventsBounties: React.FC = () => {
  const { isSignedIn } = useAuth();
  const { redirectToSignIn } = useClerk();
  const apiClient = useApiClient();
  const { toggleUserRole } = useAppContext();
  const [data, setData] = useState<EventsBountiesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [teamFormSubmitting, setTeamFormSubmitting] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api/v1';
      const response = await fetch(`${API_BASE_URL}/current-events-bounties`);
      const json = await response.json();

      if (json.success && json.data) {
        const mappedData: EventsBountiesData = {
          events: json.data.events.map((event: any) => ({
            id: event.id,
            title: event.title,
            description: event.description,
            category: event.theme,
            status: event.status,
            startDate: event.startDate,
            endDate: event.endDate,
            registrationDeadline: event.registrationDeadline,
            maxParticipants: event.maxParticipants,
            currentParticipants: event.currentParticipants,
            type: event.type,
            bounties: event.bounties.map((bounty: any) => ({
              id: bounty.id,
              title: bounty.title,
              description: bounty.description,
              prizes: bounty.value > 0 ? [{ amount: bounty.value, currency: bounty.currency }] : [],
              status: bounty.status,
              difficulty: bounty.difficulty,
              categories: bounty.tags || [],
              maxWinners: bounty.maxWinners,
              currentWinners: bounty.currentWinners,
              startDate: bounty.startDate,
              endDate: bounty.endDate,
              participants: Array.isArray(bounty.participants) ? bounty.participants.length : 0,
            })),
          })),
          totalEvents: json.data.totalEvents,
          totalBounties: json.data.totalBounties,
        };

        setData(mappedData);
        setError(null);
      } else {
        setError('Failed to fetch events and bounties');
      }
    } catch (err) {
      setError('An error occurred while fetching data');
      console.error('Error fetching public events and bounties:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterClick = async (event: Event) => {
    // If user is already signed in, show team registration form directly
    if (isSignedIn) {
      setSelectedEvent(event);
      setShowTeamForm(true);
    } else {
      // If not signed in, save pending registration with 'participant' role
      // and redirect directly to Clerk sign-in (no role selection modal)
      setPendingRegistration(event.id, event.title, 'participant');

      console.log('[PublicEventsBounties] Redirecting to sign-in with pending registration:', {
        eventId: event.id,
        eventTitle: event.title,
        role: 'participant'
      });

      await redirectToSignIn({
        redirectUrl: '/events',
      });
    }
  };

  const handleTeamRegistrationSubmit = async (teamData: TeamRegistrationData) => {
    setTeamFormSubmitting(true);

    try {
      // Automatically set user role to participant if registering for an event
      await toggleUserRole('participant');

      const response = await apiClient.createTeamRegistration(teamData);

      if (!response.success) {
        throw new Error(response.message || 'Team registration failed');
      }

      // Invalidate caches
      apiClient.invalidateCache('current-events-bounties');
      apiClient.invalidateCache('with-participation');
      apiClient.invalidateCache('user-events-bounties');
      apiClient.invalidateCache('teams');
      apiClient.invalidateCache('team-registration');

      // Close form
      setShowTeamForm(false);
      setSelectedEvent(null);
      setError(null);

      // Refetch data
      await fetchData();
    } catch (err) {
      console.error('Team registration failed:', err);
      setError(err instanceof Error ? err.message : 'Team registration failed');
      throw err;
    } finally {
      setTeamFormSubmitting(false);
    }
  };

  const handleTeamRegistrationCancel = () => {
    setShowTeamForm(false);
    setSelectedEvent(null);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Toggle event description expansion
  const toggleDescriptionExpansion = (eventId: string) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  if (loading) {
    return <CustomLoader />;
  }

  if (error) {
    return (
      <div className="text-center p-8 text-red-600">
        {error}
      </div>
    );
  }

  if (!data || data.events.length === 0) {
    return (
      <div className="text-center p-8 text-gray-600">
        No current events available
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 py-8 pb-16">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-black font-space mb-2" style={{ color: '#500295' }}>
            Current Events & Bounties
          </h2>
        </div>

        {data.events.map((event) => (
          <Card key={event.id} className="overflow-hidden bg-white">
            <CardHeader className="p-6 border-b">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <CardTitle className="text-2xl">{event.title}</CardTitle>
                  </div>

                  {/* Event Description */}
                  {event.description && (
                    <div className="mb-4">
                      <div className={`text-gray-600 ${expandedDescriptions.has(event.id) ? '' : 'line-clamp-2'} text-left`}>
                        <MarkdownRenderer content={event.description} />
                      </div>
                      {event.description.length > 150 && (
                        <button
                          onClick={() => toggleDescriptionExpansion(event.id)}
                          className="text-blue-600 hover:text-blue-700 text-xs mt-0.5 block text-left font-medium"
                        >
                          {expandedDescriptions.has(event.id) ? (
                            <>Show Less ↑</>
                          ) : (
                            <>Read More →</>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {formatDate(event.startDate)} - {formatDate(event.endDate)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>
                        {event.currentParticipants}/{event.maxParticipants} participants
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {event.category?.split(/[&,]+/).map((tag, i) => (
                        <span key={i} className={`px-3 py-1 rounded-full text-xs font-medium border ${['bg-blue-50 text-blue-700 border-blue-200', 'bg-green-50 text-green-700 border-green-200', 'bg-purple-50 text-purple-700 border-purple-200', 'bg-pink-50 text-pink-700 border-pink-200', 'bg-amber-50 text-amber-700 border-amber-200'][Math.abs(tag.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % 5]}`}>
                          {tag.trim()}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1">
                      <Trophy className="h-4 w-4" />
                      <span>{event.bounties.length} bounties</span>
                    </div>
                  </div>
                </div>

                <Button
                  size="lg"
                  className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 hover:opacity-90 text-white shrink-0"
                  onClick={() => handleRegisterClick(event)}
                >
                  Register for Event
                </Button>
              </div>
            </CardHeader>

            {/* Bounties - Always expanded */}
            {event.bounties.length > 0 && (
              <CardContent className="p-0">
                <div className="bg-gray-50 px-6 py-3 border-b">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Trophy className="h-4 w-4" />
                    <span>Available Bounties ({event.bounties.length})</span>
                  </div>
                </div>

                <div className="divide-y">
                  {event.bounties.map((bounty) => (
                    <div key={bounty.id} className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-lg">{bounty.title}</h4>
                            <Badge variant="outline" className="text-xs">
                              {bounty.difficulty}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {bounty.status}
                            </Badge>
                          </div>

                          <div className="text-gray-600 mb-3 text-left">
                            <MarkdownRenderer content={bounty.description} />
                          </div>

                          <div className="flex flex-wrap gap-2 mb-3">
                            {bounty.categories.map((category, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {category}
                              </Badge>
                            ))}
                          </div>

                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div>
                              <span className="font-medium">Winners:</span> {bounty.currentWinners}/{bounty.maxWinners}
                            </div>
                            <div>
                              <span className="font-medium">Participants:</span> {bounty.participants}
                            </div>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          {bounty.prizes.length > 0 && (
                            <div className="text-2xl font-bold text-purple-600">
                              {bounty.prizes[0].currency} {bounty.prizes[0].amount.toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </>
  );
};

export default PublicEventsBounties;
